import styled, { css } from 'styled-components'

import { DrawerFooterWrapper } from '@base'
import { Button, ErrorMessage, Input, Select } from '@baseV2'
import { Spacings, Text } from '@styles'

export const TermsToolbar = styled.div`
	display: flex;
	flex-wrap: wrap;
	justify-content: flex-end;
	gap: ${Spacings.SPACING_10};
	padding-bottom: ${Spacings.SPACING_10};
`

export const TermsGrid = styled.div`
	display: grid;
	grid-template-columns: 3fr 0.4fr 0.4fr 3.5fr auto;
`

export const RowActions = styled.div`
	display: flex;
	align-items: center;
	gap: ${Spacings.SPACING_10};
`

export const HeaderCell = styled.div`
	display: flex;
	align-items: center;
	height: ${Spacings.SPACING_11B};
	padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_10};
	background: ${({ theme }) => theme.colors.surface.overlay};
	border-top: 1px solid ${({ theme }) => theme.colors.border.default};
	border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};
	font-size: ${Text.SMALL};
	font-weight: ${Text.BOLD_500};
	color: ${({ theme }) => theme.colors.text.primary};
	white-space: nowrap;
`

export const BodyCell = styled.div`
	display: flex;
	align-items: center;
	min-height: 3.375rem;
	padding: ${Spacings.SPACING_10};
	border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};

	${({ $noHPad }) =>
		$noHPad &&
		css`
			padding-left: 0;
			padding-right: 0;
		`}
`

export const MethodChip = styled.span`
	display: inline-flex;
	align-items: center;
	width: 100%;
	height: 2.125rem;
	padding: 0 ${Spacings.SPACING_2B};
	background: ${({ theme }) => theme.colors.surface.overlay};
	border: 1px solid ${({ theme }) => theme.colors.border.default};
	border-radius: ${({ theme }) => theme.radius.sm};
	font-size: ${Text.SMALL};
	color: ${({ theme }) => theme.colors.text.primary};
`

const cellControlStyles = css`
	input {
		height: 2.125rem;
		padding-top: 0;
		padding-bottom: 0;
		font-size: ${Text.SMALL};
	}

	/* row validation surfaces only as the red error border in this compact grid — no inline message */
	${ErrorMessage} {
		display: none;
	}
`

export const CellInput = styled(Input)`
	${cellControlStyles}

	/* keep the numeric (tenure / percentage) fields compact */
	input {
		max-width: 5rem;
	}
`

export const CellSelect = styled(Select)`
	${cellControlStyles}

	/* closed state: degrade gracefully to an ellipsis instead of a hard clip */
	input {
		text-overflow: ellipsis;
		max-width: 8rem;
	}

	/* expanded options: wrap the full label instead of clipping it with an ellipsis */
	span {
		white-space: normal;
		overflow: visible;
		text-overflow: clip;
		line-height: 1.3;
	}
`

export const TypeCellContent = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: ${Spacings.SPACING_10};
	width: 100%;

	/* all chips share one fixed width so the column reads uniformly; select takes the rest */
	${MethodChip} {
		flex: 0 0 6rem;
		width: 6rem;
		justify-content: center;
		white-space: nowrap;
	}

	${CellSelect} {
		flex: 1 1 auto;
		min-width: 4.5rem;
	}
`

export const DeleteButton = styled.button`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 2.125rem;
	height: 2.125rem;
	padding: 0;
	background: transparent;
	border: none;
	border-radius: ${({ theme }) => theme.radius.sm};
	color: ${({ theme }) => theme.colors.status.error.icon};
	cursor: pointer;

	&:hover {
		color: ${({ theme }) => theme.colors.status.error.text};
	}

	svg {
		font-size: ${Text.LARGE};
	}
`

export const EmptyState = styled.p`
	padding: ${Spacings.SPACING_10} 0;
	font-size: ${Text.SMALL};
	color: ${({ theme }) => theme.colors.text.secondary};
`

export const HelperText = styled.p`
	padding-top: ${Spacings.SPACING_10};
	font-size: ${Text.SMALL};
	font-weight: ${Text.BOLD_500};
	color: ${({ theme }) => theme.colors.text.link};
`

export const FooterWrapper = styled(DrawerFooterWrapper)`
	border-top: 1px solid ${({ theme }) => theme.colors.border.default};
`

export const FooterActions = styled.div`
	display: flex;
	width: 100%;
	gap: ${Spacings.SPACING_10};
	justify-content: flex-end;
`

export const PrimaryButton = styled(Button)`
	&:disabled {
		opacity: 0.5;
	}
`
